import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Send, Settings } from "lucide-react";
import { type ChatMessage, type ChatTone } from "@/lib/bot-chat";

interface BotChatPanelProps {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  tone: ChatTone;
  onToneChange: (tone: ChatTone) => void;
  botName: string;
  isVisible: boolean;
}

export function BotChatPanel({ 
  messages, 
  onSend, 
  tone, 
  onToneChange, 
  botName,
  isVisible 
}: BotChatPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track scroll position to determine if user is at bottom
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isBottom = scrollHeight - scrollTop - clientHeight < 10; // 10px threshold
    setIsAtBottom(isBottom);
  };

  // Only auto-scroll when user is at bottom
  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isAtBottom]);

  const handleSend = () => {
    if (inputValue.trim() && tone !== 'off') {
      onSend(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isVisible) return null;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Chat with {botName}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={tone === 'off' ? 'secondary' : 'default'} className="text-xs">
              {tone === 'off' ? 'Off' : tone === 'friendly' ? 'Friendly' : 'Sassy'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {showSettings && (
          <div className="mt-2 p-2 bg-muted/50 rounded">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Tone:</span>
              <Select value={tone} onValueChange={onToneChange}>
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="sassy">Sassy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-3 p-3 min-h-0">
        <ScrollArea className="flex-1 max-h-[400px]" ref={scrollAreaRef}>
          <div className="space-y-3 pr-4" onScroll={handleScroll}>
            {tone === 'off' ? (
              <div className="text-center text-muted-foreground text-sm py-8">
                Chat is disabled. Change tone to start chatting!
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">
                No messages yet. The bot will chat during the game!
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'player' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-2 rounded-lg text-sm ${
                      message.sender === 'player'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <div className="break-words">{message.message}</div>
                    <div className={`text-xs mt-1 opacity-70 ${
                      message.sender === 'player' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {tone !== 'off' && (
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1"
              maxLength={200}
            />
            <Button 
              onClick={handleSend} 
              disabled={!inputValue.trim()}
              size="sm"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}